/**
 * <product-personalize>: toont het "kleine aanpassing"-tekstvak (verplicht) én het
 * telefoonnummer-veld (optioneel) alleen als de klant "Ja" kiest — zelfde conditie als in de
 * originele Globo-optieset. Alle velden zijn gewone, native line-item-property-inputs die al
 * meewerken met de standaard koopknop (zie snippets/product-personalize.liquid).
 *
 * Verplicht-veld-blokkade: het productformulier heeft `novalidate` (bewust, voor eigen
 * foutmeldingen i.p.v. browserbubbels), dus het `required`-attribuut alleen houdt niemand tegen.
 * Net als product-bundle.js luistert dit element naar `product-form:collect-lines`
 * (assets/product-form-lines.js) en roept `detail.cancel()` aan zodra een verplicht veld leeg is
 * — vóór de koopknop de winkelwagen bereikt, niet erna.
 */
class ProductPersonalize extends HTMLElement {
  connectedCallback() {
    this.toggleGroup = this.querySelector('[data-role="adjustment-toggle"]');
    this.textWrap = this.querySelector('[data-role="adjustment-text-wrap"]');
    this.textField = this.querySelector('[data-role="adjustment-text"]');
    this.phoneWrap = this.querySelector('[data-role="adjustment-phone-wrap"]');
    this.nameField = this.querySelector('[data-role="name-input"]');
    this.dateField = this.querySelector('[data-role="date-input"]');
    this.signatoriesField = this.querySelector('[data-role="signatories-select"]');
    this.dateOutput = this.querySelector('[data-role="date-output"]');
    this.hijriOutput = this.querySelector('[data-role="hijri-output"]');
    this.status = this.querySelector('[data-role="status"]');

    const i18nEl = this.querySelector('[data-role="i18n"]');
    this.i18n = i18nEl ? JSON.parse(i18nEl.textContent) : {};

    if (this.toggleGroup && this.textWrap && this.textField) {
      this.toggleGroup.addEventListener('change', this.onToggleChange.bind(this));
    }

    if (this.dateField && this.dateOutput) {
      this.dateField.addEventListener('input', this.syncDateOutput.bind(this));
      this.syncDateOutput();
    }

    document.addEventListener('product-form:collect-lines', (event) => this.collectLines(event.detail));

    // Express-checkoutknoppen (Apple Pay, PayPal) omzeilen collect-lines: zie syncExpressCheckout.
    this.addEventListener('input', () => this.syncExpressCheckout());
    this.addEventListener('change', () => this.syncExpressCheckout());
    document.addEventListener('click', (event) => this.onExpressClick(event), true);
    document.addEventListener('change', (event) => {
      if (event.target.name === 'id' && this.pageSection.contains(event.target)) {
        this.syncExpressCheckout();
        setTimeout(() => this.syncExpressCheckout(), 600);
      }
    });
    this.syncExpressCheckout();
    setTimeout(() => this.syncExpressCheckout(), 1500);
  }

  // Dynamische checkout-knoppen sturen het formulier niet via een submit-gebeurtenis en staan deels
  // in een iframe. Zolang een verplicht veld leeg is leggen we er een onzichtbare klikvanger over
  // (.pp-blocked, zie component-product-personalize.css); een klik daarop toont dezelfde melding.
  get expressButtons() {
    return [...this.pageSection.querySelectorAll('.shopify-payment-button')];
  }

  syncExpressCheckout() {
    const blocked = !!this.firstInvalidField(false);
    this.expressButtons.forEach((el) => el.classList.toggle('pp-blocked', blocked));
  }

  onExpressClick(event) {
    const button = event.target.closest && event.target.closest('.shopify-payment-button.pp-blocked');
    if (!button || !this.pageSection.contains(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const invalid = this.firstInvalidField(true);
    if (!invalid) return;
    this.showStatus(invalid.message, true);
    invalid.field.focus();
    invalid.field.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  // De browser levert het datumveld als ISO (2026-12-03), wat op de order dubbelzinnig oogt
  // (3 dec of 12 mrt?). Naar de winkelwagen gaat daarom een uitgeschreven datum in een
  // verborgen veld: "3 december 2026". Bewust vast Nederlands, ongeacht de taal van de klant —
  // de lezer is wijzelf. Handmatig geparsed (geen `new Date('2026-12-03')`, dat is UTC).
  syncDateOutput() {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.dateField.value);
    if (!match) {
      this.dateOutput.value = '';
      if (this.hijriOutput) this.hijriOutput.value = '';
      return;
    }
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    this.dateOutput.value = date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
    if (this.hijriOutput) this.hijriOutput.value = ProductPersonalize.toHijri(date);
  }

  // Umm al-Qura via Intl; maandnamen vast (de browser-spelling is onbetrouwbaar). Het is een
  // rekenmethode — de werkelijke dag kan door maanwaarneming een dag afwijken.
  static HIJRI_MONTHS = [
    'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani', 'Jumada al-Awwal', 'Jumada al-Thani',
    'Rajab', "Sha'ban", 'Ramadan', 'Shawwal', "Dhu al-Qa'dah", 'Dhu al-Hijjah',
  ];

  static toHijri(date) {
    try {
      const parts = {};
      new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', { day: 'numeric', month: 'numeric', year: 'numeric' })
        .formatToParts(date)
        .forEach((p) => { parts[p.type] = p.value; });
      const month = ProductPersonalize.HIJRI_MONTHS[Number(parts.month) - 1];
      return month ? `${parts.day} ${month} ${parts.year} AH` : '';
    } catch (e) {
      return '';
    }
  }

  get pageSection() {
    return this.closest('[id^="shopify-section"]') || document;
  }

  onToggleChange(event) {
    const wantsAdjustment = event.target.dataset.role === 'adjustment-yes' && event.target.checked;
    this.textWrap.hidden = !wantsAdjustment;
    this.textField.required = wantsAdjustment;
    if (!wantsAdjustment) this.textField.value = '';

    if (this.phoneWrap) {
      this.phoneWrap.hidden = !wantsAdjustment;
      if (!wantsAdjustment) this.phoneWrap.querySelector('input').value = '';
    }
  }

  showStatus(message, isError) {
    if (!this.status || !message) return;
    this.status.hidden = false;
    this.status.classList.toggle('is-error', isError);
    this.status.setAttribute('role', isError ? 'alert' : 'status');
    this.status.textContent = message;
  }

  // Geeft het eerste ongeldige veld terug (of null), en zet de foutstatus op het element zelf.
  firstInvalidField(mark = true) {
    const flag = (el) => mark && el.classList.add('is-invalid');
    // Volgorde volgt de DOM (Ondertekenaars/Naam/Datum), zodat het eerste ongeldige veld
    // ook het eerste veld is dat de klant tegenkomt bij het scrollen.
    if (this.signatoriesField && this.signatoriesField.required && !this.signatoriesField.value) {
      flag(this.signatoriesField);
      return { field: this.signatoriesField, message: this.i18n.signatoriesRequired };
    }
    if (this.nameField && !this.nameField.value.trim()) {
      flag(this.nameField);
      return { field: this.nameField, message: this.i18n.nameRequired };
    }
    if (this.textField && this.textField.required && !this.textField.value.trim()) {
      flag(this.textField);
      return { field: this.textField, message: this.i18n.adjustmentTextRequired };
    }
    if (this.dateField && this.dateField.required && !this.dateField.value) {
      flag(this.dateField);
      return { field: this.dateField, message: this.i18n.dateRequired };
    }
    return null;
  }

  collectLines(detail) {
    if (!this.pageSection.contains(detail.form)) return;

    // Opnieuw synchroniseren: browser-autofill en terugnavigeren zetten een waarde zonder `input`-event.
    if (this.dateField && this.dateOutput) this.syncDateOutput();

    [this.nameField, this.textField, this.signatoriesField, this.dateField].forEach(
      (el) => el && el.classList.remove('is-invalid')
    );

    const invalid = this.firstInvalidField();
    if (!invalid) return;

    detail.cancel();
    this.showStatus(invalid.message, true);
    invalid.field.focus();
    invalid.field.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

customElements.define('product-personalize', ProductPersonalize);
